import { z } from "zod";

import { branded } from "@/lib/helpers/branded-helper.ts";

export const escStatusSchema = z.enum(["ESC", "NON_ESC", "NOT_DETERMINED"]);
export const escTypeSchema = z.enum(["BAR_EQ_115", "BAR_TH_173"]);
export const stageSchema = z.enum([
  "NONE",
  "NON_ESC",
  "ESC_BLANK",
  "ESTIMATE_GENERATED",
  "ESTIMATE_SIGNED",
  "INSTALLATION_GENERATED",
  "INSTALLATION_SIGNED",
  "INVOICED",
]);
export const logActionTypeSchema = z.enum([
  "CASE_MIGRATION",
  "CREATED",
  "ELIGIBILITY_CHECK",
  "GENERATE_ESTIMATE",
  "GENERATE_INVOICE",
  "SEND_REMINDER",
  "GENERATE_ESC_INSTALLATION_PACKAGE",
  "GENERATE_INSTALLATION_PACKAGE",
  "GENERATE_MAINTENANCE_PACKAGE",
  "GENERATE_TRANSFER_FORM",
  "GENERATE_CUSTOMER_SERVICE_CERTIFICATE",
  "CANCEL_SIGNATURE",
  "CANCEL_ALL_SIGNATURES",
  "REGENERATE_DOCUMENT",
  "SWITCH_TO_SALE",
  "ERROR",
  "DOCUMENT_SIGNED",
  "ENDPOINT_USED",
  "GENERATE_CREDIT_NOTE",
]);

export const contextualActionTypeSchema = z.enum([
  "DOWNLOAD_DOCUMENT",
  "CANCEL_SIGNATURE",
  "SEND_REMINDER",
  "VIEW_DETAILS",
  "VIEW_ERROR",
  "REGENERATE_DOCUMENT",
  "SWITCH_TO_SALE",
  "CANCEL_ALL_SIGNATURES",
]);

export const folderInfoSchema = z.object({
  id: branded.escFolderInfoId,
  escVersion: z.number(),
  escStatus: escStatusSchema,
  escTypes: z.array(escTypeSchema),
  engagementDate: z.string().nullable(),
});

export const folderSchema = z.object({
  id: branded.escFolderId,
  siteId: z.object({ id: branded.siteId }),
  subscriberId: z.object({ id: branded.subscriberId }),
  escInfo: folderInfoSchema,
});

export const folderInvoiceSchema = z.object({
  id: branded.escFolderInvoiceId,
  escInvoiceId: z.number(),
  idSubSubscriberSignature: z.number().nullable(),
  escInvoice: z.object({
    id: z.number(),
    amount: z.number().nullable(),
    status: z.string().nullable(),
    // Add other fields as needed
  }),
});

export const folderSignatureSchema = z.object({
  id: branded.escFolderSignatureId,
  subscriberSignatureId: z.number(),
  interventionId: z.number().nullable(),
  subscriberSignature: z.object({
    id: z.number(),
    status: z.string().nullable(),
  }),
  intervention: z
    .object({
      id: z.number(),
      date: z.string().nullable(),
    })
    .nullable(),
});

export const folderLogPersonSchema = z.object({
  id: z.number().nullable(),
  firstname: z.string().nullable(),
  lastname: z.string().nullable(),
  type: z
    .object({
      id: z.number(),
      description: z.string(),
    })
    .nullable(),
});

export const folderLogSchema = z.object({
  id: branded.escFolderLogId,
  person: folderLogPersonSchema.nullable(),
  actionType: logActionTypeSchema,
  actionDatetime: z.string(),
  contextData: z.string().nullable(),
  application: z.string().nullable(),
  systemAction: z.boolean(),
  userAction: z.boolean().nullable(),
});

export const actionUrlSchema = z.object({
  url: z.string(),
  method: z.enum(["GET", "POST", "PUT", "DELETE", "PATCH"]),
  bodySchema: z.record(z.string(), z.string()).nullable(),
});

export const actionOptionSchema = z.object({
  label: z.string(),
  actionUrl: actionUrlSchema,
});

export const contextualActionSchema = z.object({
  type: contextualActionTypeSchema,
  actionUrl: actionUrlSchema.nullable(),
  options: z.array(actionOptionSchema).nullable(),
  metadata: z.any().nullable(),
});

// HistoryTree schema with recursive support

interface HistoryTreeNodeSchemaDefinition {
  log: z.infer<typeof folderLogSchema>;
  contextualActions: Array<z.infer<typeof contextualActionSchema>> | null;
  children: HistoryTreeNodeSchemaDefinition[];
}
interface HistoryTreeNodeSchemaInput {
  log: z.input<typeof folderLogSchema>;
  contextualActions: z.input<typeof contextualActionSchema>[] | null;
  children: HistoryTreeNodeSchemaInput[];
}
export const historyTreeNodeSchema: z.ZodType<
  HistoryTreeNodeSchemaDefinition,
  z.ZodTypeDef,
  HistoryTreeNodeSchemaInput
> = z.object({
  log: folderLogSchema,
  contextualActions: z.array(contextualActionSchema).nullable(),
  children: z.lazy(() => z.array(historyTreeNodeSchema)),
});

export const escFolderAggregateSchema = z.object({
  folder: folderSchema,
  historyTree: z.array(historyTreeNodeSchema),
  folderActions: z.array(contextualActionSchema).nullable(),
  stage: stageSchema,
});

export type EscStatus = z.infer<typeof escStatusSchema>;
export type EscType = z.infer<typeof escTypeSchema>;
export type Stage = z.infer<typeof stageSchema>;
export type LogActionType = z.infer<typeof logActionTypeSchema>;
export type ContextualActionTypeKey = z.infer<typeof contextualActionTypeSchema>;
export type FolderInfo = z.infer<typeof folderInfoSchema>;
export type Folder = z.infer<typeof folderSchema>;
export type FolderInvoice = z.infer<typeof folderInvoiceSchema>;
export type FolderSignature = z.infer<typeof folderSignatureSchema>;
export type EscFolderAggregate = z.infer<typeof escFolderAggregateSchema>;
export type ContextualActionType = z.infer<typeof contextualActionSchema>;
export type HistoryTreeNode = z.infer<typeof historyTreeNodeSchema>;
export type FolderLog = z.infer<typeof folderLogSchema>;
export type ESCPersonDisplay = z.infer<typeof folderLogPersonSchema>;

export interface HierarchicalEventLog extends FolderLog {
  contextualActions?: ContextualActionType[];
  children?: HierarchicalEventLog[];
}
